import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

import useToast from "../../components/useToast";
import { API_BASE_URL } from "../../config";
import AppButton from "../../design-system/components/AppButton";
import AppConfirmDialog from "../../design-system/components/AppConfirmDialog";
import AppMetaChip from "../../design-system/components/AppMetaChip";
import AppTabs from "../../design-system/components/AppTabs";
import AppText from "../../design-system/components/AppText";
import AppTextarea from "../../design-system/components/AppTextarea";
import AppWorkspace from "../../design-system/components/AppWorkspace";
import {
  IconError,
  IconSuccess,
} from "../../design-system/icons";
import ApprovalRequestsTable, {
  getApprovalCategoryMeta,
  isApprovalRowSelectable,
} from "./ApprovalRequestsTable";
import "../../design-system/styles/tokens.css";
import "../../design-system/styles/components.css";
import "./approval-flow.css";
import PunchEntryDialog from "../../components/punch_entry/PunchEntryDialog";

const TAB = {
  ALL: "all",
  SHIFT_EXCHANGE: "shift_exchange",
  PUNCH_CLOCK: "punch_clock",
  ABSENCE: "absence",
  HOLIDAY: "holiday",
  OTHER: "other",
};

const TOAST_OPTIONS = {};

const TabLabel = ({ label, count }) => (
  <span className="approval-tabs__label">
    <span>{label}</span>
    {count > 0 ? <AppMetaChip size="xs">{count > 99 ? "99+" : count}</AppMetaChip> : null}
  </span>
);

const ApprovalFlow = () => {
  const { t } = useTranslation();
  const token = localStorage.getItem("session_token");
  const { success, error } = useToast(TOAST_OPTIONS);
  // Stempelung, die die Planung aus der Genehmigung heraus korrigiert.
  const [editedPunch, setEditedPunch] = useState(null);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [tab, setTab] = useState(TAB.ALL);
  const [isLoading, setIsLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [decisionComment, setDecisionComment] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchApprovals = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/web/approval`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(response.data?.rows || []);
      setSummary(response.data?.summary || {});
    } catch (requestError) {
      error(requestError?.response?.data?.message || "Genehmigungen konnten nicht geladen werden.");
    } finally {
      setIsLoading(false);
    }
  }, [error, token]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const filteredRows = useMemo(() => {
    if (tab === TAB.ALL) return rows;
    return rows.filter((row) => (row.category || TAB.OTHER) === tab);
  }, [rows, tab]);

  /*
    Ausgewaehlt werden kann nur, was gerade auch sichtbar ist. Ein Tabwechsel
    oder ein Nachladen verwirft die Auswahl deshalb: sonst genehmigt ein Klick
    auf "Ausgewaehlte genehmigen" Anfragen, die niemand mehr vor sich hat.
  */
  const selectedRows = useMemo(() => {
    const visibleById = new Map(
      filteredRows.filter(isApprovalRowSelectable).map((row) => [row.id, row])
    );
    return selectedIds.map((id) => visibleById.get(id)).filter(Boolean);
  }, [filteredRows, selectedIds]);

  useEffect(() => {
    setSelectedIds([]);
  }, [tab]);

  const tabCounts = useMemo(() => ({
    [TAB.ALL]: summary.total || rows.length,
    [TAB.SHIFT_EXCHANGE]: summary.shift_exchange || 0,
    [TAB.PUNCH_CLOCK]: summary.punch_clock || 0,
    [TAB.ABSENCE]: summary.absence || 0,
    [TAB.HOLIDAY]: summary.holiday || 0,
  }), [rows.length, summary]);

  const closeDecisionDialog = () => {
    setDialog(null);
    setDecisionComment("");
  };

  const submitDecision = async () => {
    if (!dialog?.row || !dialog?.decision) return;
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/web/approval/decision`,
        {
          source: dialog.row.source,
          source_id: dialog.row.source_id,
          decision: dialog.decision,
          decision_comment: dialog.decision === "rejected" ? decisionComment : "",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRows(response.data?.rows || []);
      setSummary(response.data?.summary || {});
      closeDecisionDialog();
      success(dialog.decision === "approved" ? "Anfrage genehmigt." : "Anfrage abgelehnt.");
      window.dispatchEvent(new Event("approvals:changed"));
    } catch (requestError) {
      error(requestError?.response?.data?.message || "Entscheidung konnte nicht gespeichert werden.");
    } finally {
      setIsLoading(false);
    }
  };

  const submitBulkApproval = async () => {
    if (!dialog?.rows?.length) return;
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/web/approval/decision/bulk`,
        {
          decision: "approved",
          items: dialog.rows.map((row) => ({ source: row.source, source_id: row.source_id })),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRows(response.data?.rows || []);
      setSummary(response.data?.summary || {});
      setSelectedIds([]);
      closeDecisionDialog();
      const approvedCount = Number(response.data?.approved_count || 0);
      const skippedCount = Number(response.data?.skipped_count || 0);
      // Uebersprungen wird, was zwischenzeitlich jemand anderes entschieden hat.
      // Das ist kein Fehler, darf aber nicht verschwiegen werden — sonst stimmt
      // die Zahl auf dem Knopf nicht mit dem ueberein, was passiert ist.
      const approvedLabel = `${approvedCount} ${approvedCount === 1 ? "Anfrage" : "Anfragen"}`;
      success(skippedCount
        ? `${approvedLabel} genehmigt, ${skippedCount} übersprungen.`
        : `${approvedLabel} genehmigt.`);
      window.dispatchEvent(new Event("approvals:changed"));
    } catch (requestError) {
      error(requestError?.response?.data?.message || "Entscheidung konnte nicht gespeichert werden.");
    } finally {
      setIsLoading(false);
    }
  };

  const openApprovalDialog = useCallback(
    (row) => {
      setDecisionComment("");
      setDialog({ row, decision: "approved" });
    },
    []
  );
  const openRejectionDialog = useCallback(
    (row) => {
      setDecisionComment("");
      setDialog({ row, decision: "rejected" });
    },
    []
  );

  const tabs = useMemo(() => [
    { key: TAB.ALL, label: <TabLabel label="Alle" count={tabCounts[TAB.ALL]} /> },
    { key: TAB.PUNCH_CLOCK, label: <TabLabel label="Stempelung" count={tabCounts[TAB.PUNCH_CLOCK]} /> },
    { key: TAB.ABSENCE, label: <TabLabel label="Abwesenheit" count={tabCounts[TAB.ABSENCE]} /> },
    { key: TAB.HOLIDAY, label: <TabLabel label="Ferien" count={tabCounts[TAB.HOLIDAY]} /> },
    { key: TAB.SHIFT_EXCHANGE, label: <TabLabel label="Schichttausch" count={tabCounts[TAB.SHIFT_EXCHANGE]} /> },
  ], [tabCounts]);

  const dialogIsApproval = dialog?.decision === "approved";
  const dialogIsBulk = Boolean(dialog?.bulk);
  const bulkCount = dialog?.rows?.length || 0;
  const bulkQuestion = bulkCount === 1
    ? "Möchtest du die ausgewählte Anfrage genehmigen?"
    : `Möchtest du die ${bulkCount} ausgewählten Anfragen genehmigen?`;

  return (
    <div className="approval-page">
      <AppWorkspace
        title="Genehmigungen"
        loading={isLoading}
        actions={selectedRows.length ? (
          <AppButton
            variant="primary"
            size="sm"
            onClick={() => {
              setDecisionComment("");
              setDialog({ bulk: true, decision: "approved", rows: selectedRows });
            }}
          >
            <IconSuccess />
            {`Ausgewählte genehmigen (${selectedRows.length})`}
          </AppButton>
        ) : null}
      >
        <AppTabs items={tabs} activeKey={tab} onChange={setTab} variant="page" />

        <ApprovalRequestsTable
          rows={filteredRows}
          onApprove={openApprovalDialog}
          onReject={openRejectionDialog}
          onEdit={(row) => setEditedPunch({
            id: row.target_id,
            row,
            employeeName: row.requester_name,
            checkIn: row.start_at,
            checkOut: row.end_at,
            breakMinutes: row.break_minutes,
          })}
          selectedIds={selectedIds}
          onSelectedIdsChange={setSelectedIds}
        />
      </AppWorkspace>

      {/*
        Genehmigen speichert die Korrektur und genehmigt in einem Aufruf
        (punch_changes); Ablehnen führt in den bekannten Dialog mit Begründung.
      */}
      <PunchEntryDialog
        entry={editedPunch}
        onClose={() => setEditedPunch(null)}
        onApprove={async (punchChanges) => {
          const row = editedPunch.row;
          const response = await axios.post(
            `${API_BASE_URL}/api/web/approval/decision`,
            {
              source: row.source,
              source_id: row.source_id,
              decision: "approved",
              decision_comment: "",
              punch_changes: punchChanges,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setRows(response.data?.rows || []);
          setSummary(response.data?.summary || {});
          setSelectedIds([]);
          success("Anfrage mit Korrektur genehmigt.");
          window.dispatchEvent(new Event("approvals:changed"));
        }}
        onReject={() => {
          const row = editedPunch.row;
          setEditedPunch(null);
          openRejectionDialog(row);
        }}
      />

      <AppConfirmDialog
        open={Boolean(dialog)}
        size="sm"
        title={dialogIsBulk
          ? "Ausgewählte Anfragen genehmigen"
          : (dialogIsApproval ? "Anfrage genehmigen" : "Anfrage ablehnen")}
        description={dialogIsBulk
          ? bulkQuestion
          : (dialogIsApproval
            ? "Möchtest du diese Anfrage wirklich genehmigen?"
            : "Möchtest du diese Anfrage wirklich ablehnen?")}
        confirmLabel={dialogIsBulk
          ? "Genehmigen"
          : t(dialogIsApproval ? "button.approve" : "button.reject")}
        confirmIcon={dialogIsApproval ? <IconSuccess /> : <IconError />}
        confirmTone={dialogIsApproval ? "default" : "error"}
        cancelLabel={t("button.cancel")}
        onClose={closeDecisionDialog}
        onConfirm={dialogIsBulk ? submitBulkApproval : submitDecision}
      >
        <div className="approval-dialog__content">
          <p className="ds-dialog__message">
            {dialogIsBulk
              ? bulkQuestion
              : (dialogIsApproval
                ? "Möchtest du diese Anfrage wirklich genehmigen?"
                : "Möchtest du diese Anfrage wirklich ablehnen?")}
          </p>
          {!dialogIsApproval ? (
            <AppTextarea
              className="approval-rejection-comment"
              value={decisionComment}
              onChange={(event) => setDecisionComment(event.target.value)}
              rows={1}
              wrap="soft"
              maxLength={1000}
              placeholder="Kommentar hinzufügen"
              aria-label="Ablehnungsgrund"
            />
          ) : null}
          {dialog?.row ? (
            <div className="approval-dialog__summary">
              <AppText variant="label-1">
                {dialog.row.title || getApprovalCategoryMeta(dialog.row.category).label}
              </AppText>
              <AppText variant="body-2" tone="secondary">
                {dialog.row.requester_name} · {dialog.row.period}
              </AppText>
              {dialog.row.break_minutes != null ? (
                <AppText variant="body-2" tone="secondary">
                  Pause: {dialog.row.break_minutes} Min.
                </AppText>
              ) : null}
              {dialog.row.comment ? (
                <AppText variant="body-2" tone="secondary">
                  {dialog.row.comment}
                </AppText>
              ) : null}
            </div>
          ) : null}
        </div>
      </AppConfirmDialog>
    </div>
  );
};

export default ApprovalFlow;
